import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { DepartmentFilter } from '@/modules/settings/organization/departments';

export interface LeaveCalendarFilters {
  /** The day to go to ("YYYY-MM-DD"). */
  day: string;
  departmentId: string;
  search: string;
}

interface LeaveCalendarFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What is applied now; each opening starts from it. */
  applied: LeaveCalendarFilters;
  onApply: (filters: LeaveCalendarFilters) => void;
}

/**
 * The calendar's filters on a phone, as the card tables' filter dialog: the day, department and name
 * search in one dialog, applied together on "Apply"; "Clear" removes the department and search and
 * keeps the day shown.
 */
export const LeaveCalendarFilterDialog = ({
  open,
  onOpenChange,
  applied,
  onApply,
}: LeaveCalendarFilterDialogProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(applied);
  const [wasOpen, setWasOpen] = useState(open);
  // Each opening starts from what is applied, not from a draft left unapplied last time.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(applied);
  }

  const apply = () => {
    onApply({ ...draft, search: draft.search.trim() });
    onOpenChange(false);
  };

  const clear = () => {
    onApply({ day: applied.day, departmentId: '', search: '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent alignCloseWithTitle>
        <DialogHeader>
          <DialogTitle>{t('FILTER')}</DialogTitle>
        </DialogHeader>

        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <div className="flex flex-col gap-3">
            <Label>{t('DATE')}</Label>
            <DatePicker
              className="w-full"
              aria-label={t('DATE')}
              value={draft.day}
              onChange={(day) => day && setDraft((current) => ({ ...current, day }))}
            />
          </div>
          <div className="flex flex-col gap-3">
            <Label>{t('DEPARTMENT')}</Label>
            <DepartmentFilter
              value={draft.departmentId}
              onChange={(departmentId) => setDraft((current) => ({ ...current, departmentId }))}
              selectClassName="w-full"
            />
          </div>
          <div className="flex flex-col gap-3">
            <Label>{t('SEARCH_BY_NAME')}</Label>
            <Input
              placeholder={t('SEARCH_BY_NAME')}
              aria-label={t('SEARCH_BY_NAME')}
              value={draft.search}
              onChange={(event) =>
                setDraft((current) => ({ ...current, search: event.target.value }))
              }
            />
          </div>
          {/* Enter in the search applies, like the card tables' filter fields. */}
          <button type="submit" hidden />
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={clear}>
            {t('CLEAR')}
          </Button>
          <Button type="button" onClick={apply}>
            {t('APPLY')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
